import * as React from 'react';
import { ChangeEvent, ReactElement, useState } from 'react';
import InformationTable from 'components/common/table/InformationTable';
import {
  BackOrderOfferCreditSubscriptionType,
  BackOrderSubscriptionType,
  ClosetSubscriptionType,
  PlanInformation,
} from 'types/api/backorder';
import BackOrderSelect from 'components/backorder/table/subscription/BackOrderSelect';
import OfferSubscriptionTerm from 'components/backorder/table/subscription/OfferSubscriptionTerm';
import OfferCredits from 'components/backorder/table/subscription/OfferCredits';
import { TextField } from '@material-ui/core';
import styled from '@emotion/styled';
import Button from 'components/common/Button';
import ErrorModal from 'components/common/modal/ErrorModal';
import { AxiosError } from 'axios';
import { ErrorResult } from 'types/api';
import ConfirmModal from 'components/common/modal/ConfirmModal';
import { backOrderSubscriptionName } from 'constants/backorder';
import { subscribeBackOrder } from 'api/backorder';
import {
  getRangePickerMappingDates,
  subscriptionTerms,
  TODAY_FOR_SUBSCRIPTION,
} from 'constants/backorder/rangePickerMapperDates';
import { SearchDateTerms } from 'types/common/searchfilter';

interface Props {
  groupId: string;
  currentPlan: PlanInformation;
}

export const initialSubscriptionTerm: [string, string] = [
  TODAY_FOR_SUBSCRIPTION,
  getRangePickerMappingDates({
    startDate: TODAY_FOR_SUBSCRIPTION,
    subscriptionTerms: subscriptionTerms[SearchDateTerms['1_MONTH']],
  }),
];
const BackOrderSubscribe = ({ groupId, currentPlan }: Props): ReactElement => {
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<
    BackOrderSubscriptionType | ''
  >('');
  const [subscriptionTerm, setSubscriptionTerm] =
    useState<[string, string]>(initialSubscriptionTerm);
  const [offerCredit, setOfferCredit] = useState<number>(0);
  const [commentValue, setCommentValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmModalVisibility, setConfirmModalVisibility] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<{ code: number | string; message: string } | null>(
    null,
  );
  const offerCreditVisibility =
    selectedSubscriptionType == ClosetSubscriptionType.STANDARD_INVOICE_PAYMENT ||
    selectedSubscriptionType === ClosetSubscriptionType.PREMIUM_INVOICE_PAYMENT;
  const canSubscribe =
    selectedSubscriptionType !== '' &&
    commentValue !== '' &&
    (offerCreditVisibility ? offerCredit !== 0 : true);

  const onChangeSubscriptionItem = (e: ChangeEvent<{ name?: string; value: unknown }>): void => {
    if (!e.target.value) return;
    setSelectedSubscriptionType(e.target.value as BackOrderSubscriptionType);
  };

  const onChangeOfferCredit = (value: number): void => {
    setOfferCredit(value);
  };

  const onChangeComment = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentValue(value);
  };

  const onSubscribe = async (): Promise<void> => {
    setIsLoading(true);
    setConfirmModalVisibility(false);
    try {
      await subscribeBackOrder({
        groupId,
        comment: commentValue,
        subscription: {
          plan: selectedSubscriptionType as BackOrderSubscriptionType,
          credit: offerCredit,
          startDate: subscriptionTerm[0],
          endDate: subscriptionTerm[1],
        },
      });
      window.location.reload();
    } catch (e) {
      const error = e as AxiosError<ErrorResult>;
      if (!error.response?.data) {
        setErrorStatus({
          code: error.code ?? 500,
          message: error.message,
        });
        return;
      }
      setErrorStatus({
        code: error.response.data.resultCode,
        message: error.response.data.resultMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const TableDate = [
    {
      title: 'Back Order Item',
      requiredPoint: true,
      render: (
        <BackOrderSelect
          subscriptionId={currentPlan.subscriptionId}
          currentPlan={currentPlan.plan}
          selectedSubscriptionType={selectedSubscriptionType}
          onChangeSubscriptionItem={onChangeSubscriptionItem}
        />
      ),
    },
    {
      title: 'Offer Subscription Term',
      render: (
        <OfferSubscriptionTerm
          disabled={selectedSubscriptionType === ''}
          subscriptionTerm={subscriptionTerm}
          setSubscriptionTerm={setSubscriptionTerm}
        />
      ),
    },
    {
      title: 'Offer Credit',
      requiredPoint: true,
      render: (
        <OfferCredits
          groupId={groupId}
          subscriptionPlan={selectedSubscriptionType as BackOrderOfferCreditSubscriptionType}
          subscriptionTerm={subscriptionTerm}
          offerCredit={offerCredit}
          onChangeOfferCredit={onChangeOfferCredit}
        />
      ),
      isShow: offerCreditVisibility,
    },
    {
      title: 'Comment',
      requiredPoint: true,
      render: (
        <TextField
          fullWidth
          multiline
          rows={4}
          variant={'outlined'}
          value={commentValue}
          onChange={onChangeComment}
          placeholder={'Please enter the reason for this offer.'}
          inputProps={{
            maxLength: 500,
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Container>
        <InformationTable title={'Back Order'} data={TableDate} />
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() => setConfirmModalVisibility(true)}
          disabled={!canSubscribe}
          isLoading={isLoading}
        >
          Subscribe
        </Button>
      </Container>
      {confirmModalVisibility && (
        <ConfirmModal
          open
          onClose={() => setConfirmModalVisibility(false)}
          onConfirm={onSubscribe}
          description={`Are you sure you want to provide the ${
            backOrderSubscriptionName[selectedSubscriptionType as BackOrderSubscriptionType]
          } for this Group?`}
        />
      )}
      {errorStatus && (
        <ErrorModal content={errorStatus.message} onClose={() => setErrorStatus(null)} isOpen />
      )}
    </>
  );
};

export default BackOrderSubscribe;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;
